import React, { useContext, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../../Auth/contexts/AuthContext';
import { NAVBAR_ITEMS, CLIENT_NAVBAR_ITEMS } from '../../Routes';
import { ReactComponent as Logo } from '../../../../assets/TrndsLogo.svg';
import { ReactComponent as LogoutIcon } from '../../../../assets/Logout.svg';
import styles from './NavBar.module.css';
import { makeUserHasGroupAccess } from '../../../../helpers';
import cn from 'classnames';
import { NavBarItem, isNavBarItemGroup } from '../../helpers';
import ClientSelect from './components/ClientSelect';

export function NavBar() {
    const authContext = useContext(AuthContext);
    const userHasGroupAccess = useMemo(
        () => makeUserHasGroupAccess(authContext.user?.groups ?? []),
        [authContext.user?.groups]
    );
    const isUserTrndsClientUser = useMemo(
        () => authContext.user?.groups.includes('trnds_client'),
        [authContext.user?.groups]
    );
    const location = useLocation();

    const title = useMemo(() => {
        const group = NAVBAR_ITEMS.find(
            (item) => isNavBarItemGroup(item) && !!item.items.find((item) => location.pathname.includes(item.url))
        );

        const route = NAVBAR_ITEMS.map((item) => {
            if (isNavBarItemGroup(item)) {
                return item.items;
            }

            return item;
        })
            .flat()
            .find((route) => location.pathname.includes(route.url));

        if (group && route) {
            return `${route.title} | ${group.title} | trnds`;
        }

        if (route) {
            return `${route.title} | trnds`;
        }

        return 'trnds';
    }, [location.pathname]);

    const navBarToDisplay = useMemo(() => {
        if (authContext.loading || !authContext.authenticated) {
            return [];
        }

        const relevantNavBarItems = isUserTrndsClientUser ? CLIENT_NAVBAR_ITEMS : NAVBAR_ITEMS;

        return relevantNavBarItems.reduce<NavBarItem[]>((navBar, item) => {
            if (isNavBarItemGroup(item) && item.items.some((item) => userHasGroupAccess(item.groups, 'all'))) {
                item.items = item.items.filter((i) => {
                    return userHasGroupAccess(i.groups, 'all');
                });
                navBar.push(item);
            }

            if (!isNavBarItemGroup(item) && userHasGroupAccess(item.groups, 'all')) {
                navBar.push(item);
            }

            return navBar;
        }, []);
    }, [authContext.authenticated, authContext.loading, userHasGroupAccess, isUserTrndsClientUser]);

    return (
        <>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <div className={styles.navBar}>
                <div className={styles.navBarSection}>
                    <div className={styles.logoContainer}>
                        <Logo width={80} className={styles.logo} />
                    </div>
                </div>
                <div className={cn(styles.navBarSection, styles.stretchSection)}>
                    <div className={styles.links}>
                        {navBarToDisplay.map((item) => {
                            if (isNavBarItemGroup(item)) {
                                const activeLink = item.items.find((link) => location.pathname === link.url);

                                return (
                                    <div
                                        key={item.title}
                                        className={cn(styles.navItem, styles.link, { [styles.active]: !!activeLink })}
                                    >
                                        {item.title}
                                        <div className={styles.dropdown}>
                                            {item.items.map((link) => (
                                                <Link
                                                    key={link.title}
                                                    to={link.url}
                                                    className={cn(styles.link, {
                                                        [styles.active]: activeLink?.url === link.url,
                                                    })}
                                                >
                                                    {link.title}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                );
                            }

                            const isActive = location.pathname === item.url;
                            return (
                                <Link
                                    key={item.title}
                                    to={item.url}
                                    className={cn(styles.navItem, styles.link, {
                                        [styles.active]: isActive,
                                    })}
                                >
                                    {item.title}
                                </Link>
                            );
                        })}
                    </div>
                </div>
                <div className={styles.navBarSection}>
                    <ClientSelect />
                </div>
                <div className={styles.navBarSection}>
                    <div
                        className={styles.logout}
                        onClick={() => {
                            if (!authContext.loading && authContext.authenticated) {
                                authContext.logout();
                            }
                        }}
                    >
                        <span>Logout</span>
                        <LogoutIcon className={styles.logoutIcon} width={20} height={20} />
                    </div>
                </div>
            </div>
        </>
    );
}
